import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Text,
  Card,
  Badge,
  SearchInput,
  FilterChips,
  StatusDot,
  EmptyState,
} from '@teals/ui-web';
import { useGetAgentsQuery, type Agent } from '../../store/api';

type AgentScope = Agent['scope'];

const SCOPE_BADGES: Record<AgentScope, { variant: 'primary' | 'info' | 'warning'; label: string }> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
};

const FILTER_CHIPS = [
  { id: 'all', label: 'All' },
  { id: 'global', label: 'Global' },
  { id: 'organisation', label: 'Organisation' },
  { id: 'application', label: 'Application' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const AgentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const AgentCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

const CardDescription = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.5rem;
  border-top: 1px solid var(--border);
`;

const UpdatedText = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const LoadingState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const Agents: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const { data: agents = [], isLoading, isError } = useGetAgentsQuery();

  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      const matchesSearch =
        !search || agent.name.toLowerCase().includes(search.toLowerCase());
      const matchesScope = scopeFilter === 'all' || agent.scope === scopeFilter;
      return matchesSearch && matchesScope;
    });
  }, [agents, search, scopeFilter]);

  const chipsWithCounts = useMemo(
    () =>
      FILTER_CHIPS.map((chip) => ({
        ...chip,
        count:
          chip.id === 'all'
            ? agents.length
            : agents.filter((a) => a.scope === chip.id).length,
      })),
    [agents]
  );

  return (
    <Page>
      <Header>
        <Text variant="h3">Agents</Text>
        <Button variant="primary" onClick={() => navigate('/agents/new')}>
          Create Agent
        </Button>
      </Header>

      <Toolbar>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search agents..."
        />
        <FilterChips
          items={chipsWithCounts}
          activeId={scopeFilter}
          onChange={setScopeFilter}
        />
      </Toolbar>

      {isLoading ? (
        <LoadingState>Loading agents…</LoadingState>
      ) : isError ? (
        <EmptyState
          title="Failed to load agents"
          description="Could not reach the agents API. Check your organisation context and try again."
        />
      ) : filteredAgents.length === 0 ? (
        <EmptyState
          title="No agents found"
          description={
            search
              ? `No agents match "${search}". Try adjusting your search or filters.`
              : 'No agents available for this organisation yet.'
          }
          action={
            search ? (
              <Button
                variant="outline"
                onClick={() => {
                  setSearch('');
                  setScopeFilter('all');
                }}
              >
                Clear Filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <AgentGrid>
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              padding={20}
              elevation="sm"
              onClick={() => navigate(`/agents/${encodeURIComponent(agent.id)}`)}
            >
              <CardHeader>
                <Text variant="h5" style={{ margin: 0 }}>
                  {agent.name}
                </Text>
                <Badge variant={SCOPE_BADGES[agent.scope].variant} size="sm">
                  {SCOPE_BADGES[agent.scope].label}
                </Badge>
              </CardHeader>
              <CardDescription>{agent.description}</CardDescription>
              <CardFooter>
                <StatusDot status={agent.versionStatus} label={agent.versionStatus} />
                <UpdatedText>{agent.version}</UpdatedText>
              </CardFooter>
            </AgentCard>
          ))}
        </AgentGrid>
      )}
    </Page>
  );
};

export default Agents;
